import React, { useLayoutEffect, useRef, useState } from 'react';

/**
 * A segmented control whose "on" fill springs from option to option. The
 * labels are drawn twice: once in the resting colour, and once inverted (on
 * the accent) in a layer clipped to the active option. Moving the clip moves
 * the fill, and every glyph stays legible mid-slide, which a sliding pill
 * behind black-on-white text does not manage.
 */
export function Segments({ label, value, options, onChange, className = '', itemClassName = '', render = (o) => o.label }) {
  // Measured on the root, the box the lit layer covers: the active option's
  // offsets include the root's padding, so its far insets must too.
  const rootRef = useRef(null);
  const clip = useActiveRect(rootRef, '[aria-checked="true"]', [value, options.length]);
  return (
    <div className={`edl-seg ${className}`} role="radiogroup" aria-label={label} ref={rootRef}>
      <div className="edl-seg__track">
        {options.map((o) => (
          <button key={o.value} type="button" role="radio" aria-checked={value === o.value} className={`edl-seg__item ${itemClassName}`} onClick={() => onChange(o.value)} title={o.title}>
            {render(o)}
          </button>
        ))}
      </div>
      <div
        className={`edl-seg__lit${clip.ready ? ' is-ready' : ''}`}
        aria-hidden="true"
        style={clip.rect ? { clipPath: `inset(${clip.rect.top}px ${clip.rect.right}px ${clip.rect.bottom}px ${clip.rect.left}px round 100px)` } : { clipPath: 'inset(50% 50% 50% 50%)' }}
      >
        {options.map((o) => (
          <span key={o.value} className={`edl-seg__item ${itemClassName}`}>
            {render(o)}
          </span>
        ))}
      </div>
    </div>
  );
}

/**
 * The active child's box as insets from the container, kept current through
 * resizes (fonts loading, labels changing). `ready` turns on transitions after
 * the first placement, so a control does not animate in from nowhere.
 */
export function useActiveRect(ref, selector, deps) {
  const [state, setState] = useState({ rect: null, ready: false });
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    const place = () => {
      const active = el.querySelector(selector);
      if (!active) return setState((s) => ({ ...s, rect: null }));
      const W = el.clientWidth;
      const H = el.clientHeight;
      const r = { left: active.offsetLeft, top: active.offsetTop, width: active.offsetWidth, height: active.offsetHeight };
      setState((s) => ({ ...s, rect: { ...r, right: W - r.left - r.width, bottom: H - r.top - r.height } }));
    };
    place();
    const raf = requestAnimationFrame(() => setState((s) => (s.ready ? s : { ...s, ready: true })));
    const ro = new ResizeObserver(place);
    ro.observe(el);
    for (const c of el.children) ro.observe(c);
    return () => {
      cancelAnimationFrame(raf);
      ro.disconnect();
    };
  }, deps);
  return state;
}

/**
 * A hover highlight that follows the pointer between rows of a list, the way
 * a hand moves down it, instead of each row lighting up on its own. It fades
 * in where the pointer enters and glides from there. Vanilla DOM: no render
 * per pointer move. Render `<div ref={glowRef} className="edl-hover-glide" />`
 * as the host's first child.
 */
export function useHoverGlide(ref, glowRef, selector) {
  useLayoutEffect(() => {
    const host = ref.current;
    const glow = glowRef.current;
    if (!host || !glow) return undefined;
    let current = null;
    const show = (row) => {
      const hr = host.getBoundingClientRect();
      const rr = row.getBoundingClientRect();
      const visible = glow.classList.contains('is-on');
      if (!visible) glow.classList.add('is-jump');
      glow.style.transform = `translate(${rr.left - hr.left + host.scrollLeft}px, ${rr.top - hr.top + host.scrollTop}px)`;
      glow.style.width = `${rr.width}px`;
      glow.style.height = `${rr.height}px`;
      if (!visible) {
        glow.getBoundingClientRect(); // commit the jump before the fade
        glow.classList.remove('is-jump');
      }
      glow.classList.add('is-on');
    };
    const onOver = (e) => {
      const row = e.target.closest?.(selector);
      if (!row || !host.contains(row) || row === current) return;
      current = row;
      show(row);
    };
    const onLeave = () => {
      current = null;
      glow.classList.remove('is-on');
    };
    host.addEventListener('pointerover', onOver);
    host.addEventListener('pointerleave', onLeave);
    return () => {
      host.removeEventListener('pointerover', onOver);
      host.removeEventListener('pointerleave', onLeave);
    };
  }, []);
}
